'use client';

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { subscriptionApi } from '@/lib/subscriptionApi';
import api from '@/lib/api';

interface AgreementDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  agreementType: 'music-distribution-agreement' | 'terms-of-service' | 'privacy-policy' | 'ai-creation-guidelines';
}

interface AgreementData {
  title: string;
  content: string;
  version?: string;
  effective_date?: string | null;
}

export default function AgreementDrawer({
  isOpen,
  onClose,
  agreementType
}: AgreementDrawerProps) {
  const [agreement, setAgreement] = useState<AgreementData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen) closeButtonRef.current?.focus();
  }, [isOpen]);

  // 将前端协议类型格式（连字符）转换为后端格式（下划线）
  const convertAgreementType = (type: string): string => {
    return type.replace(/-/g, '_');
  };

  // 从 API 获取协议内容
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const loadAgreement = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // 转换协议类型格式
        const backendType = convertAgreementType(agreementType);
        
        // 根据协议类型选择API：服务条款、隐私政策和AI创作指南使用公开API（无需认证），其他使用订阅API（需要认证）
        let response;
        if (agreementType === 'terms-of-service' || agreementType === 'privacy-policy' || agreementType === 'ai-creation-guidelines') {
          // 使用公开API（无需认证）
          response = await api.auth.getPublicAgreement(backendType as 'terms_of_service' | 'privacy_policy' | 'ai_creation_guidelines');
        } else {
          // 使用订阅API（需要认证）
          response = await subscriptionApi.getAgreementVersion(backendType);
        }
        
        if (response.success && response.data) {
          setAgreement({
            title: response.data.title || '协议',
            content: response.data.content || '',
            version: response.data.version,
            effective_date: response.data.effective_date
          });
        } else {
          setError('获取协议内容失败');
        }
      } catch (err) {
        console.error('加载协议内容失败:', err);
        setError(err instanceof Error ? err.message : '加载协议内容失败');
      } finally {
        setLoading(false);
      }
    };

    loadAgreement();
  }, [isOpen, agreementType]);

  // 如果协议内容未加载，显示加载状态或错误
  if (!agreement && !loading && !error) {
    return null;
  }

  const content = (
    <>
      {/* 遮罩层 */}
      {isOpen && (
        <div
          onClick={onClose}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            transition: 'opacity 0.3s ease'
          }}
        />
      )}

      {/* Drawer：桌面右侧抽屉，移动端全屏 */}
      <div
        className="agreement-drawer-panel"
        style={{
          position: 'fixed',
          top: 0,
          right: isOpen ? 0 : '-100%',
          width: '500px',
          maxWidth: '90vw',
          height: '100vh',
          backgroundColor: '#ffffff',
          boxShadow: '-2px 0 8px rgba(0, 0, 0, 0.1)',
          transition: 'right 0.3s ease',
          zIndex: 10000,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {/* 头部 */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0
        }}>
          <div style={{ flex: 1 }}>
            <h2 style={{
              fontSize: '1.125rem',
              fontWeight: '600',
              color: '#000000',
              margin: 0
            }}>
              {loading ? '加载中...' : error ? '加载失败' : agreement?.title || '协议'}
            </h2>
          </div>
          <button
            type="button"
            ref={closeButtonRef}
            onClick={onClose}
            aria-label="关闭"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.5rem',
              minWidth: 44,
              minHeight: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#666666',
              transition: 'color 0.2s ease',
              touchAction: 'manipulation'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = '#000000';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = '#666666';
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* 内容区域 */}
        <div 
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '1.5rem',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none'
          }}
          onContextMenu={(e) => e.preventDefault()}
          onCopy={(e) => e.preventDefault()}
          onCut={(e) => e.preventDefault()}
        >
          {loading ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              color: '#666666',
              fontSize: '0.875rem'
            }}>
              加载中...
            </div>
          ) : error ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              color: '#ef4444',
              fontSize: '0.875rem',
              textAlign: 'center',
              gap: '0.5rem'
            }}>
              <div>{error}</div>
              <button
                onClick={() => {
                  setError(null);
                  // 重新加载
                  const loadAgreement = async () => {
                    setLoading(true);
                    try {
                      const backendType = convertAgreementType(agreementType);
                      let response;
                      if (agreementType === 'terms-of-service' || agreementType === 'privacy-policy' || agreementType === 'ai-creation-guidelines') {
                        response = await api.auth.getPublicAgreement(backendType as 'terms_of_service' | 'privacy_policy' | 'ai_creation_guidelines');
                      } else {
                        response = await subscriptionApi.getAgreementVersion(backendType);
                      }
                      if (response.success && response.data) {
                        setAgreement({
                          title: response.data.title || '协议',
                          content: response.data.content || '',
                          version: response.data.version,
                          effective_date: response.data.effective_date
                        });
                      } else {
                        setError('获取协议内容失败');
                      }
                    } catch (err) {
                      setError(err instanceof Error ? err.message : '加载协议内容失败');
                    } finally {
                      setLoading(false);
                    }
                  };
                  loadAgreement();
                }}
                style={{
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  color: '#000000',
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.375rem',
                  cursor: 'pointer'
                }}
              >
                重试
              </button>
            </div>
          ) : agreement ? (
            <div 
              style={{
                fontSize: '0.875rem',
                lineHeight: '1.8',
                color: '#000000',
                whiteSpace: 'pre-wrap',
                fontFamily: 'inherit',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none'
              }}
              onContextMenu={(e) => e.preventDefault()}
              onCopy={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
            >
              {agreement.content}
            </div>
          ) : null}
        </div>

        {/* 底部按钮 */}
        <div style={{
          padding: '1.5rem',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          gap: '1rem',
          flexShrink: 0
        }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              flex: 1,
              padding: '0.75rem 1.5rem',
              minHeight: 44,
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#000000',
              backgroundColor: '#f5f5f5',
              border: '1px solid #e5e7eb',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              touchAction: 'manipulation'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#e5e5e5';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#f5f5f5';
            }}
          >
            关闭
          </button>
        </div>
      </div>
    </>
  );

  const portalTarget =
    typeof document !== 'undefined' ? document.getElementById('drawer-portal-root') : null;
  return portalTarget ? createPortal(content, portalTarget) : content;
}
